import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { registrarCapsula } from '../../api/client'
import { hoyLocal } from '../partes/fechas'
import { hora } from './tiempo'

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// "Registrar tiempo": work done earlier, typed in by hand -- a day, a start
// and an end. The server splits it into normal and overtime at the edges of
// the jornada; overtime needs the authorization box ticked.
// personas / partes: what can be picked; persona / parte: fixed ones.
export default function RegistrarModal({ personas = [], partes = [], persona, parte, onClose, onHecho }) {
  const { t } = useTranslation('engineering')
  const hoy = hoyLocal()
  const [personaId, setPersonaId] = useState(persona ? String(persona.id) : '')
  const [parteId, setParteId] = useState(parte ? String(parte.id) : '')
  const [fecha, setFecha] = useState(hoy)
  const [inicio, setInicio] = useState('08:00')
  const [fin, setFin] = useState('')
  const [nota, setNota] = useState('')
  const [autorizar, setAutorizar] = useState(false)
  const [pideExtra, setPideExtra] = useState(null)    // the jornada, once the server says it's needed
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (!personaId || !parteId || !fecha || !inicio || !fin) { setError(t('capsulas.registrar.faltan')); return }
    if (fin <= inicio) { setError(t('capsulas.registrar.finAntes')); return }
    setGuardando(true)
    setError('')
    try {
      const caps = await registrarCapsula({
        persona_id: Number(personaId), parte_id: Number(parteId),
        inicio: `${fecha}T${inicio}:00`, fin: `${fecha}T${fin}:00`,
        autorizar_hora_extra: autorizar, nota: nota.trim() || null,
      })
      onHecho?.(caps)
      onClose()
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'fuera_de_horario') { setPideExtra(d.jornada || false); setError(t('capsulas.registrar.pideExtra')) }
      else if (d?.code === 'solapada') setError(t('capsulas.registrar.solapada'))
      else if (d?.code === 'fuera_del_parte') setError(t('capsulas.registrar.fueraDelParte'))
      else if (typeof d === 'string' && d.includes('future')) setError(t('capsulas.registrar.futuro'))
      else setError(t('capsulas.aviso.fallo'))
    }
    setGuardando(false)
  }

  return (
    <Modal title={t('capsulas.registrar.titulo')} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-xs text-gray-500">{t('capsulas.registrar.hint')}</p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('capsulas.persona')} *</label>
          {persona ? <p className="text-sm font-medium text-gray-900">{persona.nombre}</p> : (
            <select className={inputCls} value={personaId} onChange={e => setPersonaId(e.target.value)}>
              <option value="">{t('partes.abrir.pickOption')}</option>
              {personas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('capsulas.parte')} *</label>
          {parte ? <p className="text-sm font-medium text-gray-900">#{parte.numero} {parte.unidad_codigo} · {parte.nombre}</p> : (
            <select className={inputCls} value={parteId} onChange={e => setParteId(e.target.value)}>
              <option value="">{t('partes.abrir.pickOption')}</option>
              {partes.map(p => <option key={p.id} value={p.id}>#{p.numero} · {p.unidad_codigo} · {p.nombre}</option>)}
            </select>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('capsulas.registrar.dia')} *</label>
            <input type="date" className={inputCls} value={fecha} max={hoy} onChange={e => setFecha(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('capsulas.registrar.desde')} *</label>
            <input type="time" className={inputCls} value={inicio} onChange={e => setInicio(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('capsulas.registrar.hasta')} *</label>
            <input type="time" className={inputCls} value={fin} onChange={e => setFin(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('capsulas.nota')}</label>
          <input className={inputCls} value={nota} onChange={e => setNota(e.target.value)} placeholder={t('capsulas.notaPlaceholder')} />
        </div>
        <label className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer ${
          pideExtra !== null ? 'bg-amber-50 border border-amber-300' : 'bg-gray-50 border border-gray-100'}`}>
          <input type="checkbox" className="mt-0.5" checked={autorizar} onChange={e => setAutorizar(e.target.checked)} />
          <span>
            <span className="font-medium text-gray-800">{t('capsulas.registrar.autorizar')}</span>
            <span className="block text-xs text-gray-500">
              {pideExtra
                ? t('capsulas.extra.fuera', { inicio: hora(pideExtra.inicio), fin: hora(pideExtra.fin) })
                : pideExtra === false ? t('capsulas.extra.noLaboral') : t('capsulas.registrar.autorizarHint')}
            </span>
          </span>
        </label>
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose}
            className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
            {t('unidades.cancel')}
          </button>
          <button type="submit" disabled={guardando}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
            {guardando ? t('unidades.saving') : t('capsulas.registrar.guardar')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
